import React, { useEffect, useState } from "react";
import {
  Typography,
  Container,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  useMediaQuery
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CelebrationIcon from "@mui/icons-material/Celebration";
import SideMenu from "../components/SideMenu";
import { useTheme } from "@mui/material/styles";
import ConsultantForm from "../components/ConsultantForm";
import CreditsForm from "../components/CreditsForm";
import DocumentsForm from "../components/DocumentsForm";
import DebtReasons from "../components/DebtReasons";
import { useSelector } from "react-redux";
import { getConsultantByUserId } from "../services/consultantServices";
import { getCreditByConsultant } from "../services/creditServices";
import { getReasonsByConsultant } from "../services/reasonsServices";
import { getDocumentByConsultant } from "../services/docsServices";
import { REQUIRED_DOCS } from "../constants";
import Loading from "../components/Loading";

const STEPS = [
  {
    label: "Información personal y laboral",
    description:
      "Esta información nos será de utilidad para evaluar tu situación específica y poder ofrecer la mejor solución posible en tu asesoría con base a tu perfil personal."
  },
  {
    label: "Razones de endeudamiento",
    description:
      "Nos gustaría saber cuáles crees que fueron las razones por las cuales te endeudaste. Esto nos ayuda a entender tu situación."
  },
  {
    label: "Información de créditos",
    description:
      "Utilizaremos esta información para conocer tu situación financiera actual y poder ayudarte a saldar tus créditos. Puedes agregar todos los que tengas."
  },
  {
    label: "Documentos",
    description:
      "Carga los documentos obligatorios para tu asesoría. La plataforma te indicará cuáles son necesarios y cómo obtenerlos."
  }
];

const UserDashboard = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const userData = useSelector((state) => state.user.user);
  const [consultant, setConsultant] = useState({});
  const [credits, setCredits] = useState([]);
  const [reasons, setReasons] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docsCompleted, setDocsCompleted] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [showLoading, setShowLoading] = useState(false);
  const [allComplete, setAllComplete] = useState(false);

  useEffect(() => {
    let ignore = false;
    async function fetchData() {
      setShowLoading(true);
      if (userData.id) {
        const response = await getConsultantByUserId(userData.id);
        if (!ignore) {
          setConsultant(response);
        }
      }
    }
    fetchData();
    return () => {
      ignore = true;
    };
  }, [userData]);

  useEffect(() => {
    let ignore = false;
    async function fetchData() {
      setShowLoading(true);
      if (consultant && consultant.id) {
        const creditsData = await getCreditByConsultant(consultant.id);
        const reasonsData = await getReasonsByConsultant(consultant.id);
        const docsData = await getDocumentByConsultant(consultant.id);
        if (!ignore) {
          setCredits(creditsData);
          setReasons(reasonsData);
          setDocs(docsData);
        }
      }
      setTimeout(() => {
        setShowLoading(false);
      }, 1200);
    }
    fetchData();
    return () => {
      ignore = true;
    };
  }, [consultant]);

  useEffect(() => {
    const hasAllRequiredDocs = REQUIRED_DOCS.every((requiredDoc) =>
      docs.some((doc) => doc.type === requiredDoc)
    );
    setDocsCompleted(hasAllRequiredDocs);
  }, [docs]);

  const isStepComplete = (step) => {
    switch (step) {
      case 0:
        return !!consultant.completedAt;
      case 1:
        return reasons && !!reasons.createdAt;
      case 2:
        return credits.length > 0;
      case 3:
        return docsCompleted;
      default:
        return false;
    }
  };

  useEffect(() => {
    const complete = [0, 1, 2, 3].every(isStepComplete);
    setAllComplete(complete);

    if (!showLoading && !complete) {
      const firstIncomplete = [0, 1, 2, 3].find((s) => !isStepComplete(s));
      if (firstIncomplete !== undefined) {
        setActiveStep(firstIncomplete);
      }
    }
    // eslint-disable-next-line
  }, [consultant, reasons, credits, docsCompleted, showLoading]);

  const handleNext = () => setActiveStep((prev) => Math.min(prev + 1, 3));
  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));
  const handleStepClick = (step) => () => {
    if (step <= getMaxReachableStep()) {
      setActiveStep(step);
    }
  };

  const getMaxReachableStep = () => {
    for (let i = 0; i < 4; i++) {
      if (!isStepComplete(i)) return i;
    }
    return 3;
  };

  const canAdvance = isStepComplete(activeStep) && activeStep < 3;

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <ConsultantForm consultant={consultant} onModelChange={setConsultant} />;
      case 1:
        return (
          <DebtReasons
            reasons={reasons}
            consultantId={consultant.id}
            onModelChange={setReasons}
          />
        );
      case 2:
        return (
          <CreditsForm
            creditList={credits}
            consultantId={consultant.id}
            onModelChange={setCredits}
          />
        );
      case 3:
        return <DocumentsForm docs={docs} consultantId={consultant.id} onModelChange={setDocs} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ backgroundColor: theme.palette.pageBackground, minHeight: "100vh" }}>
      <div style={{ display: "flex" }}>
        <SideMenu />
        <main style={{ flexGrow: 1, padding: "24px" }}>
          <div style={{ minHeight: "64px" }} />
          <Container>
            <Typography variant="h4" gutterBottom>
              {`¡Bienvenido, ${userData.name} ${userData.lastname}!`}
            </Typography>

            {/* Intro card */}
            <Paper sx={{ p: 2.5, mb: 3 }}>
              <Typography>
                Gracias por tu interés en tomar una asesoría con FFCI. Completa los siguientes pasos
                para poder brindarte un buen servicio. Cuando hayas completado todos, podrás agendar
                tu primera cita de asesoría.
              </Typography>
            </Paper>

            {allComplete ? (
              /* Celebration Moment */
              <Paper
                elevation={3}
                sx={{
                  textAlign: "center",
                  py: 6,
                  px: 4,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.success.light}15)`
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    backgroundColor: "success.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3
                  }}
                >
                  <CelebrationIcon sx={{ fontSize: 44, color: "white" }} />
                </Box>
                <Typography variant="h4" gutterBottom fontWeight={600}>
                  ¡Felicidades!
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  Has completado toda la información necesaria para tu asesoría.
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: "auto" }}>
                  Ya puedes agendar tu primera cita. Un asesor revisará tu información y te guiará
                  en el proceso.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  href="https://wa.me/+50259749230"
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<WhatsAppIcon />}
                  sx={{
                    backgroundColor: "#25D366",
                    color: "white",
                    px: 5,
                    py: 1.5,
                    fontSize: "1.1rem",
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "#1DA851"
                    }
                  }}
                >
                  Agendar cita por WhatsApp
                </Button>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
                  WhatsApp: 5974 9230
                </Typography>

                {/* Mini step summary */}
                <Box sx={{ mt: 5, display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
                  {STEPS.map((step, idx) => (
                    <Paper
                      key={idx}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        borderColor: "success.main",
                        cursor: "pointer"
                      }}
                      onClick={() => {
                        setAllComplete(false);
                        setActiveStep(idx);
                      }}
                    >
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Typography variant="body2">{step.label}</Typography>
                    </Paper>
                  ))}
                </Box>
              </Paper>
            ) : (
              /* Stepper Wizard */
              <>
                {/* Horizontal stepper for desktop, vertical labels for mobile */}
                <Stepper
                  activeStep={activeStep}
                  alternativeLabel={!isSmallScreen}
                  orientation={isSmallScreen ? "vertical" : "horizontal"}
                  sx={{ mb: 3 }}
                  nonLinear
                >
                  {STEPS.map((step, index) => {
                    const completed = isStepComplete(index);
                    const reachable = index <= getMaxReachableStep();
                    return (
                      <Step key={step.label} completed={completed}>
                        <StepLabel
                          onClick={reachable ? handleStepClick(index) : undefined}
                          sx={{
                            cursor: reachable ? "pointer" : "default",
                            "& .MuiStepLabel-label": {
                              fontWeight: activeStep === index ? 600 : 400
                            }
                          }}
                          StepIconProps={{
                            ...(completed && {
                              icon: <CheckCircleIcon color="success" />
                            })
                          }}
                        >
                          {step.label}
                        </StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>

                {/* Step content */}
                <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                  <Typography variant="h5" gutterBottom>
                    {`${activeStep + 1}. ${STEPS[activeStep].label}`}
                  </Typography>
                  <Typography color="text.secondary" paragraph>
                    {STEPS[activeStep].description}
                  </Typography>
                  {getStepContent(activeStep)}
                </Paper>

                {/* Navigation */}
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    disabled={activeStep === 0}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={handleNext}
                    disabled={!canAdvance}
                  >
                    Siguiente
                  </Button>
                </Box>
              </>
            )}
          </Container>
          <Loading show={showLoading} />
        </main>
      </div>
    </Box>
  );
};

export default UserDashboard;
